import { useEffect, useState } from 'react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { DataTable, Column } from '@/components/DataTable';
import { Modal } from '@/components/Modal';
import { FormField } from '@/components/FormField';
import { tiktokService, TiktokAd, TiktokAdGroup } from '@/services/tiktok.service';
import { LastSyncedBadge } from '@/components/LastSyncedBadge';
import { Megaphone, RefreshCw, ExternalLink, Power, Plus, Upload, Wand2, Play, ImageIcon, Film } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

type AdFormat = 'SINGLE_VIDEO' | 'SINGLE_IMAGE' | 'CAROUSEL_ADS' | 'COLLECTION_ADS';
type Identity = { identityId: string; identityType: string; displayName?: string; profileImage?: string };

const CTA_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'LEARN_MORE',   label: 'Learn more' },
  { value: 'SHOP_NOW',     label: 'Shop now' },
  { value: 'SIGN_UP',      label: 'Sign up' },
  { value: 'DOWNLOAD_NOW', label: 'Download' },
  { value: 'CONTACT_US',   label: 'Contact us' },
  { value: 'BOOK_NOW',     label: 'Book now' },
  { value: 'APPLY_NOW',    label: 'Apply now' },
  { value: 'WATCH_NOW',    label: 'Watch now' },
];

const inputCls =
  'w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary';

const STATUS_COLORS: Record<string, string> = {
  ENABLE: 'bg-success-soft text-success',
  DISABLE: 'bg-surface-alt text-text-muted',
};

type Props = {
  /** Rendered inside TiktokAdsManager's tab shell — suppress the page Topbar. */
  embedded?: boolean;
  /** When set, only ads belonging to this ad group are listed. */
  adGroupId?: string;
};

export default function TiktokAds({ embedded = false, adGroupId }: Props = {}) {
  const [ads, setAds] = useState<TiktokAd[] | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  // imageId / videoId -> renderable thumbnail URL. Resolved lazily per ad list.
  const [thumbs, setThumbs] = useState<Record<string, string>>({});

  // ── Create-ad modal state ──────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [adGroups, setAdGroups] = useState<TiktokAdGroup[]>([]);
  const [identities, setIdentities] = useState<Identity[]>([]);
  const [enhancing, setEnhancing] = useState(false);
  const [form, setForm] = useState<{
    tiktokAdGroupId: string;
    adName: string;
    adFormat: AdFormat;
    adText: string;
    callToAction: string;
    landingPageUrl: string;
    identityId: string;
    // Source-url inputs — uploaded into TikTok at submit time and converted
    // to imageId / videoId. Avoids two-step UX where user uploads first.
    creativeUrl: string;
    // Filled after upload — display the resolved id so user can verify.
    resolvedImageId: string;
    resolvedVideoId: string;
  }>({
    tiktokAdGroupId: '',
    adName: '',
    adFormat: 'SINGLE_VIDEO',
    adText: '',
    callToAction: 'LEARN_MORE',
    landingPageUrl: '',
    identityId: '',
    creativeUrl: '',
    resolvedImageId: '',
    resolvedVideoId: '',
  });

  const resetForm = () => {
    setForm({
      tiktokAdGroupId: adGroups[0]?.tiktokAdGroupId ?? '',
      adName: '',
      adFormat: 'SINGLE_VIDEO',
      adText: '',
      callToAction: 'LEARN_MORE',
      landingPageUrl: '',
      identityId: identities[0]?.identityId ?? '',
      creativeUrl: '',
      resolvedImageId: '',
      resolvedVideoId: '',
    });
  };

  const openCreate = async () => {
    try {
      const [groups, idents] = await Promise.all([
        tiktokService.listAdGroups(),
        tiktokService.listIdentities().catch(() => [] as Identity[]),
      ]);
      setAdGroups(groups);
      setIdentities(idents);
      if (groups.length === 0) {
        toast.error('Create an ad group first — ads attach to one.');
        return;
      }
      setForm((f) => ({
        ...f,
        tiktokAdGroupId: f.tiktokAdGroupId || groups[0].tiktokAdGroupId,
        identityId: f.identityId || idents[0]?.identityId || '',
      }));
    } catch {
      toast.error('Could not load ad groups / identities');
      return;
    }
    setCreateOpen(true);
  };

  const uploadCreative = async () => {
    const url = form.creativeUrl.trim();
    if (!url) { toast.error('Paste an image / video URL first'); return; }
    setUploading(true);
    try {
      if (form.adFormat === 'SINGLE_IMAGE') {
        const r = await tiktokService.uploadImageByUrl({ imageUrl: url });
        setForm((f) => ({ ...f, resolvedImageId: r.imageId, resolvedVideoId: '' }));
        toast.success(`Image uploaded · id ${r.imageId}`);
      } else if (form.adFormat === 'SINGLE_VIDEO') {
        const r = await tiktokService.uploadVideoByUrl({ videoUrl: url });
        setForm((f) => ({ ...f, resolvedVideoId: r.videoId, resolvedImageId: '' }));
        toast.success(`Video uploaded · id ${r.videoId}`);
      } else {
        toast.error('Carousel/Collection ads need multiple creatives — not supported in this dialog yet.');
      }
    } catch {
      // axios interceptor already toasted
    } finally { setUploading(false); }
  };

  const enhance = async () => {
    setEnhancing(true);
    try {
      const ident = identities.find((i) => i.identityId === form.identityId);
      // Enhance always uses the company default AI model (set on /settings/ai)
      // — keeps enhancement style consistent across the app. Generation flows
      // still let the user pick a model.
      const r = await tiktokService.enhanceAdCopy({
        adFormat: form.adFormat,
        draftAdName: form.adName || undefined,
        draftAdText: form.adText || undefined,
        draftCallToAction: form.callToAction || undefined,
        brandName: ident?.displayName,
        landingPageUrl: form.landingPageUrl || undefined,
      });
      setForm((f) => ({
        ...f,
        adName: r.adName || f.adName,
        adText: r.adText || f.adText,
        callToAction: r.callToAction || f.callToAction,
      }));
      toast.success('Enhanced with AI');
    } catch {
      // Axios interceptor toasts the backend message; avoid duplicates.
    } finally { setEnhancing(false); }
  };

  const submitCreate = async () => {
    if (!form.tiktokAdGroupId) { toast.error('Pick an ad group'); return; }
    if (!form.adName.trim()) { toast.error('Ad name is required'); return; }
    if (!form.adText.trim()) { toast.error('Ad text is required'); return; }
    if (!form.identityId) { toast.error('Pick a publishing identity'); return; }
    const targetGroup = adGroups.find((g) => g.tiktokAdGroupId === form.tiktokAdGroupId);
    if (form.adFormat === 'SINGLE_IMAGE' && (targetGroup?.placements ?? ['PLACEMENT_TIKTOK']).includes('PLACEMENT_TIKTOK')) {
      // TikTok in-feed doesn't serve single-image ads; API rejects with "The source of this post is invalid".
      toast.error("Single-image ads don't serve on the TikTok placement — use a video instead."); return;
    }
    if (form.adFormat === 'SINGLE_IMAGE' && !form.resolvedImageId) {
      toast.error('Upload an image first'); return;
    }
    if (form.adFormat === 'SINGLE_VIDEO' && !form.resolvedVideoId) {
      toast.error('Upload a video first'); return;
    }

    setCreating(true);
    try {
      const r = await tiktokService.createAd({
        tiktokAdGroupId: form.tiktokAdGroupId,
        adName: form.adName.trim(),
        adFormat: form.adFormat,
        adText: form.adText.trim(),
        callToAction: form.callToAction,
        landingPageUrl: form.landingPageUrl.trim() || undefined,
        videoId: form.resolvedVideoId || undefined,
        imageIds: form.resolvedImageId ? [form.resolvedImageId] : undefined,
        identityId: form.identityId,
        identityType: identities.find((i) => i.identityId === form.identityId)?.identityType,
      });
      toast.success(`Ad created · TikTok id ${r.adId}`);
      setCreateOpen(false);
      resetForm();
      await silentSync();
      await refresh();
    } catch {
      // axios interceptor toasts the backend message
    } finally { setCreating(false); }
  };

  const refresh = () =>
    tiktokService
      .listAds(adGroupId ? { adGroupId } : undefined)
      .then(setAds)
      .catch(() => setAds([]));

  // Auto-sync on mount when data is empty or 10+ min stale.
  useEffect(() => {
    refresh().then(() => {
      tiktokService.listAds()
        .then((rows) => {
          const newest = rows
            .map((r) => Date.parse(r.lastSyncedAt ?? ''))
            .filter((n) => Number.isFinite(n))
            .reduce((a, b) => Math.max(a, b), 0);
          const stale = !newest || Date.now() - newest > 10 * 60 * 1000;
          if (rows.length === 0 || stale) silentSync();
        })
        .catch(() => undefined);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adGroupId]);

  const silentSync = async () => {
    try {
      await tiktokService.syncAds();
      await refresh();
    } catch { /* user can retry via the manual button */ }
  };

  // Ads only store TikTok asset ids (videoId / imageIds), so the thumbnail has
  // to be resolved separately: images come from the creatives library (prefer
  // `imagekitUrl` — TikTok's own `imageUrl` is signed and expires within hours),
  // videos from /creatives/videos/:id/status which returns a cover frame.
  useEffect(() => {
    if (!ads || ads.length === 0) return;
    let cancelled = false;

    const wantedImages = new Set<string>();
    const wantedVideos = new Set<string>();
    ads.forEach((a) => {
      const img = a.imageIds?.[0];
      if (img && !thumbs[img]) wantedImages.add(img);
      if (a.videoId && !thumbs[a.videoId]) wantedVideos.add(a.videoId);
    });
    if (wantedImages.size === 0 && wantedVideos.size === 0) return;

    (async () => {
      const found: Record<string, string> = {};

      if (wantedImages.size > 0) {
        const lib = await tiktokService.listImages({ pageSize: 200 }).catch(() => []);
        lib.forEach((i) => {
          if (wantedImages.has(i.imageId)) {
            const url = i.imagekitUrl ?? i.imageUrl;
            if (url) found[i.imageId] = url;
          }
        });
      }

      // One call per distinct video — deduped above, and failures are ignored so
      // a single unresolvable asset can't blank the whole list.
      await Promise.all(
        Array.from(wantedVideos).map(async (vid) => {
          const r = await tiktokService.videoStatus(vid).catch(() => null);
          if (r?.coverUrl) found[vid] = r.coverUrl;
        }),
      );

      if (!cancelled && Object.keys(found).length > 0) {
        setThumbs((prev) => ({ ...prev, ...found }));
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ads]);

  const toggleStatus = async (a: TiktokAd) => {
    setBusyId(a.tiktokAdId);
    const next = a.operationStatus === 'ENABLE' ? 'DISABLE' : 'ENABLE';
    try {
      await tiktokService.setAdStatusV2(a.tiktokAdId, next);
      toast.success(`Ad ${next === 'ENABLE' ? 'enabled' : 'paused'}`);
      await refresh();
    } catch {
      // Axios interceptor already toasted the backend message; avoid duplicates.
    } finally { setBusyId(null); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await tiktokService.syncAds();
      toast.success(`Synced ${r.total} ads — ${r.created} new, ${r.updated} updated`);
      await refresh();
    } catch {
      // Axios interceptor already toasted the backend message; avoid duplicates.
    } finally { setSyncing(false); }
  };


  const columns: Column<TiktokAd>[] = [
    {
      key: 'creative',
      header: 'Creative',
      width: '76px',
      render: (a) => <AdCreativeThumb ad={a} thumbs={thumbs} />,
    },
    {
      key: 'ad',
      header: 'Ad',
      width: 'minmax(220px, 2.4fr)',
      render: (a) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-text-primary">{a.adName}</div>
          {a.adText && <div className="mt-0.5 line-clamp-2 text-xs text-text-secondary">{a.adText}</div>}
          <div className="mt-0.5 truncate font-mono text-2xs text-text-muted">{a.tiktokAdId}</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: 'minmax(88px, 0.6fr)',
      render: (a) => a.operationStatus ? (
        <span className={clsx('inline-block rounded-full px-2 py-0.5 text-2xs font-semibold', STATUS_COLORS[a.operationStatus] ?? 'bg-surface-alt text-text-muted')}>
          {a.operationStatus}
        </span>
      ) : <span className="text-text-muted">—</span>,
    },
    {
      key: 'format',
      header: 'Format',
      width: 'minmax(110px, 0.8fr)',
      render: (a) => a.adFormat ? (
        <span className="inline-block rounded-full bg-info-soft px-2 py-0.5 text-2xs text-info">
          {a.adFormat.replace(/_/g, ' ')}
        </span>
      ) : <span className="text-text-muted">—</span>,
    },
    {
      key: 'cta',
      header: 'CTA',
      width: 'minmax(90px, 0.7fr)',
      render: (a) => a.callToAction
        ? <span className="truncate text-xs text-text-secondary">{a.callToAction.replace(/_/g, ' ')}</span>
        : <span className="text-text-muted">—</span>,
    },
    {
      key: 'landing',
      header: 'Landing',
      width: 'minmax(80px, 0.6fr)',
      render: (a) => a.landingPageUrl ? (
        <a
          href={a.landingPageUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-info hover:underline"
          title={a.landingPageUrl}
        >
          Open <ExternalLink size={10} />
        </a>
      ) : <span className="text-text-muted">—</span>,
    },
    {
      key: 'actions',
      header: '',
      width: 'minmax(100px, auto)',
      render: (a) => (
        <div className="flex items-center justify-end">
          <button
            onClick={() => toggleStatus(a)}
            disabled={busyId === a.tiktokAdId}
            className={clsx(
              'inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors',
              a.operationStatus === 'ENABLE'
                ? 'border-success/30 bg-success-soft text-success hover:bg-success-soft/70'
                : 'border-border bg-surface-alt text-text-muted hover:bg-border',
              busyId === a.tiktokAdId && 'cursor-wait opacity-60',
            )}
          >
            <Power size={11} /> {a.operationStatus === 'ENABLE' ? 'Pause' : 'Enable'}
          </button>
        </div>
      ),
    },
  ];

  const actions = (
    <div className="flex items-center gap-2">
      <LastSyncedBadge timestamps={(ads ?? []).map(a => a.lastSyncedAt)} />
      <Button variant="secondary" onClick={sync} loading={syncing}><RefreshCw size={14} /> Sync</Button>
      <Button variant="primary" onClick={openCreate}><Plus size={14} /> New ad</Button>
    </div>
  );

  return (
    <>
      {embedded ? (
        <div className="flex justify-end px-7 pt-4">{actions}</div>
      ) : (
        <Topbar
          title="TikTok Ads"
          breadcrumb="TikTok Ads"
          description="Individual creative ads inside your ad groups."
          right={actions}
        />
      )}

      <div className={clsx('w-full space-y-4 px-7', embedded ? 'pb-24 pt-4' : 'py-7')}>
        {ads === null && <Card><div className="skeleton h-24 rounded-lg" /></Card>}

        {ads && ads.length === 0 && (
          <EmptyState
            icon={<Megaphone size={40} className="text-text-muted" />}
            title="No ads yet"
            description="Ads sit inside ad groups. Create one here, or build it in TikTok Ads Manager and Sync."
            action={
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button variant="primary" onClick={openCreate}><Plus size={14} /> New ad</Button>
                <Button variant="secondary" onClick={sync} loading={syncing}><RefreshCw size={14} /> Sync now</Button>
                <a href="https://ads.tiktok.com/i18n/dashboard/campaign" target="_blank" rel="noreferrer">
                  <Button variant="ghost"><ExternalLink size={14} /> Open Ads Manager</Button>
                </a>
              </div>
            }
          />
        )}

        {ads && ads.length > 0 && (
          <DataTable rows={ads} columns={columns} stickyHeader />
        )}
      </div>

      {/* ── Create ad modal ───────────────────────────────────────────────── */}
      <Modal
        open={createOpen}
        onClose={() => !creating && !uploading && setCreateOpen(false)}
        title="New TikTok ad"
        widthClass="max-w-2xl"
      >
        <div className="space-y-4">
          <p className="rounded-lg border border-border bg-surface-alt/50 p-3 text-xs text-text-secondary">
            An ad combines your <strong>creative</strong> (image or video), your <strong>identity</strong> (the publishing
            account), and a <strong>landing page + CTA</strong>.
          </p>

          {/* AI enhance bar */}
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-primary/30 bg-primary-soft/40 p-2.5">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary text-white">
              <Wand2 size={12} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold text-text-primary">Enhance with AI</div>
              <div className="text-2xs text-text-muted">
                Type rough drafts in the ad name / ad text / CTA fields below, then click Enhance. Uses your default model from <a className="underline" href="/settings/ai">Settings → AI</a>.
              </div>
            </div>
            <Button
              variant="primary"
              onClick={enhance}
              loading={enhancing}
            >
              <Wand2 size={12} /> Enhance
            </Button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Ad group" required>
              <select
                className={inputCls}
                value={form.tiktokAdGroupId}
                onChange={(e) => setForm((f) => ({ ...f, tiktokAdGroupId: e.target.value }))}
              >
                {adGroups.map((g) => (
                  <option key={g.tiktokAdGroupId} value={g.tiktokAdGroupId}>
                    {g.adGroupName}
                  </option>
                ))}
              </select>
            </FormField>

            <FormField label="Ad name" required>
              <input
                className={inputCls}
                placeholder="e.g. Eid · Hero · v1"
                value={form.adName}
                onChange={(e) => setForm((f) => ({ ...f, adName: e.target.value }))}
              />
            </FormField>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Ad format" required>
              <select
                className={inputCls}
                value={form.adFormat}
                onChange={(e) => setForm((f) => ({
                  ...f,
                  adFormat: e.target.value as AdFormat,
                  // Clear any creatives uploaded under the previous format
                  resolvedImageId: '',
                  resolvedVideoId: '',
                }))}
              >
                {/* Live-verified: image creatives are rejected on every
                    placement for this advertiser ("The source of this post is
                    invalid"), so only video is selectable. */}
                <option value="SINGLE_IMAGE" disabled>Single image — not accepted by TikTok here</option>
                <option value="SINGLE_VIDEO">Single video</option>
              </select>
              <p className="mt-1 text-2xs text-text-muted">
                TikTok rejects image creatives on this advertiser — use a video ad.
              </p>
            </FormField>

            <FormField label="Identity" required hintBelow hint="The TikTok account that will publish the ad.">
              <select
                className={inputCls}
                value={form.identityId}
                onChange={(e) => setForm((f) => ({ ...f, identityId: e.target.value }))}
              >
                {identities.length === 0 ? (
                  <option value="">— No identities found —</option>
                ) : (
                  identities.map((i) => (
                    <option key={i.identityId} value={i.identityId}>
                      {i.displayName ?? i.identityId} ({i.identityType})
                    </option>
                  ))
                )}
              </select>
            </FormField>
          </div>

          {/* Creative upload-by-URL */}
          <FormField
            label={form.adFormat === 'SINGLE_VIDEO' ? 'Video URL' : 'Image URL'}
            required
            hintBelow hint="Paste a public URL to a test image / video. We'll upload it to TikTok and use the resolved id."
          >
            <div className="flex gap-2">
              <input
                className={clsx(inputCls, 'flex-1')}
                type="url"
                placeholder="https://example.com/creative.jpg"
                value={form.creativeUrl}
                onChange={(e) => setForm((f) => ({ ...f, creativeUrl: e.target.value }))}
              />
              <Button variant="secondary" onClick={uploadCreative} loading={uploading} disabled={!form.creativeUrl.trim()}>
                <Upload size={13} /> Upload
              </Button>
            </div>
            {(form.resolvedImageId || form.resolvedVideoId) && (
              <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-success-soft px-2 py-0.5 font-mono text-2xs text-success">
                ✓ Uploaded ·{' '}
                {form.resolvedImageId ? `image_id ${form.resolvedImageId}` : `video_id ${form.resolvedVideoId}`}
              </div>
            )}
          </FormField>

          <FormField label="Ad text" required hintBelow hint="Primary copy shown above the creative. Max ~100 chars.">
            <textarea
              rows={2}
              className={clsx(inputCls, 'resize-none')}
              placeholder="Big sale this weekend. Tap to shop."
              value={form.adText}
              onChange={(e) => setForm((f) => ({ ...f, adText: e.target.value }))}
            />
          </FormField>

          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Call to action">
              <select
                className={inputCls}
                value={form.callToAction}
                onChange={(e) => setForm((f) => ({ ...f, callToAction: e.target.value }))}
              >
                {CTA_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </FormField>

            <FormField label="Landing page URL" hintBelow hint="Where the CTA sends the user.">
              <input
                className={inputCls}
                type="url"
                placeholder="https://your-site.com/landing"
                value={form.landingPageUrl}
                onChange={(e) => setForm((f) => ({ ...f, landingPageUrl: e.target.value }))}
              />
            </FormField>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
            <Button variant="ghost" onClick={() => { resetForm(); setCreateOpen(false); }} disabled={creating || uploading}>Cancel</Button>
            <Button variant="primary" onClick={submitCreate} loading={creating}>Create ad</Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

// Shows the actual image / video cover attached to the ad. Falls back to a
// format-appropriate placeholder while the asset resolves (or if TikTok can't
// return a cover — e.g. a video still transcoding, where `displayable` is false).
const AdCreativeThumb: React.FC<{ ad: TiktokAd; thumbs: Record<string, string> }> = ({ ad, thumbs }) => {
  const isVideo = !!ad.videoId;
  const key = ad.videoId ?? ad.imageIds?.[0];
  const url = key ? thumbs[key] : undefined;

  if (url) {
    return (
      <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-border bg-surface-alt">
        <img
          src={url}
          alt={ad.adName}
          loading="lazy"
          className="h-full w-full object-cover"
          // A signed TikTok URL can expire between list and render; drop the
          // broken image rather than showing a torn-image glyph.
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }}
        />
        {isVideo && (
          <span className="absolute inset-0 flex items-center justify-center bg-black/25">
            <Play size={14} className="text-white drop-shadow" />
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      className="flex h-14 w-14 shrink-0 flex-col items-center justify-center gap-0.5 rounded-lg border border-dashed border-border bg-surface-alt text-text-muted"
      title={key ? `Asset ${key} — preview unavailable` : 'No creative attached'}
    >
      {isVideo ? <Film size={15} /> : key ? <ImageIcon size={15} /> : <Megaphone size={15} />}
      <span className="text-[9px] leading-none">{isVideo ? 'video' : key ? 'image' : 'none'}</span>
    </div>
  );
};
